import { useCallback, useEffect, useMemo, useState } from 'react';

import { AUCTION_HOUSE_VIEW_MORE_SIZE } from '@/pages/Search/SearchPage/types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setSearchExclusions } from '@/redux/modules/search/exclusions/searchExclusions.actions';
import { getUserExcludedHouses } from '@/redux/modules/search/exclusions/searchExclusions.selectors';
import { fetchFacetViewMore } from '@/redux/modules/search/search.actions';
import { getAggregationsOnDemandStatus, getLastSearchFetchOptions } from '@/redux/modules/search/search.selectors';
import { ElasticAggregateDemandStatus } from '@/redux/modules/search/search.types';
import { getMergedOptionFacet } from '@/redux/modules/searchFacetAggregates.selectors';
import { fetchSellersIfNeeded, getHousesById } from '@/redux/modules/seller';
import FacetTypes from '@/types/search/enums/facetTypes';

type ExcludedHouse = {
    auctionHouseName: string;
    sellerId: number;
};

type HousesToExcludeData = {
    auctionHouseName: string | null;
    selected?: boolean;
    sellerId: string | null;
    totalResults: number | null;
};

type UseExcludeHousesReturn = {
    excludedHouseIds: number[];
    handleApplyExcludeHouses: () => Promise<void>;
    handleRemoveHouse: (sellerId: number) => void;
    handleSelectHouse: (sellerId: string) => void;
    hasExcludedHousesChanges: boolean;
    hasHousesToExcludeChanges: boolean;
    housesById: ReturnType<typeof getHousesById>;
    housesToExcludeData: HousesToExcludeData[];
    isLoadingHouses: boolean;
    localExcludedHouses: ExcludedHouse[];
    selectedHousesToExclude: string[];
};

export const useExcludeHouses = (): UseExcludeHousesReturn => {
    const dispatch = useAppDispatch();
    const excludedHouseIds = useAppSelector((state) => getUserExcludedHouses(state));
    const housesById = useAppSelector((state) => getHousesById(state, excludedHouseIds));

    const auctionHouseFacet = useAppSelector((state) => getMergedOptionFacet(state, FacetTypes.AUCTION_HOUSE_ID));
    const hasSearchToReplay = useAppSelector((state) => Boolean(getLastSearchFetchOptions(state)));
    const aggregationsOnDemandStatus = useAppSelector(getAggregationsOnDemandStatus);

    const housesFacets = useMemo(
        () =>
            ((auctionHouseFacet?.categories && auctionHouseFacet?.options) || []).map((option) => ({
                auctionHouseName: option.name || null,
                sellerId: option.id?.toString() || null,
                totalResults: option.count || null,
            })),
        [auctionHouseFacet]
    );

    const [localExcludedHouses, setLocalExcludedHouses] = useState<ExcludedHouse[]>(() =>
        housesById.map((house) => ({
            auctionHouseName: house.name,
            sellerId: house.sellerId,
        }))
    );

    const [selectedHousesToExclude, setSelectedHousesToExclude] = useState<string[]>([]);
    const [isLoadingHouses, setIsLoadingHouses] = useState(hasSearchToReplay);

    const hasExcludedHousesChanges = useMemo(() => {
        const currentHouseIds = new Set(localExcludedHouses.map((house) => house.sellerId));
        return excludedHouseIds.some((id) => !currentHouseIds.has(id));
    }, [localExcludedHouses, excludedHouseIds]);

    const hasHousesToExcludeChanges = useMemo(() => {
        return selectedHousesToExclude.length > 0;
    }, [selectedHousesToExclude]);

    const housesToExcludeData = useMemo(
        () =>
            housesFacets.map((house) => ({
                ...house,
                selected: selectedHousesToExclude.includes(house.sellerId || ''),
            })),
        [housesFacets, selectedHousesToExclude]
    );

    // The modal reads the auctionHouse facet straight from Redux, where the
    // live search request caps it at 6 buckets (the drawer's "fade and peek"
    // ViewMore size). Replay the last search with the view-more Size cap so the
    // full house list is available to exclude — the same fetch the AllFilters
    // drawer fires on its "View More" click. Fires once per surface mount (the
    // modal / drawer sub-view mount fresh each open); the fulfilled handler only
    // merges facets, so the results behind the surface are untouched. Skip when
    // there's no prior search to replay — the thunk would no-op.
    //
    // Both entry points dispatch fetchSearchAggregationsOnDemand right before
    // opening, and its fulfilled handler *wholesale-replaces* state.facets with
    // auctionHouse back at the default 6. If we fired in parallel and won the
    // race, that hydration would land after us and clobber our expansion. So
    // wait until it's no longer Loading — then our merge lands last and sticks.
    useEffect(() => {
        if (!hasSearchToReplay || aggregationsOnDemandStatus === ElasticAggregateDemandStatus.Loading) {
            return;
        }
        // Set here too, not just via the initial state, so a re-fire (e.g. a
        // background search flips hasSearchToReplay false->true after mount)
        // shows the spinner instead of silently swapping the list in place.
        setIsLoadingHouses(true);
        void dispatch(
            fetchFacetViewMore({ facetId: FacetTypes.AUCTION_HOUSE_ID, size: AUCTION_HOUSE_VIEW_MORE_SIZE })
        ).finally(() => {
            setIsLoadingHouses(false);
        });
    }, [dispatch, hasSearchToReplay, aggregationsOnDemandStatus]);

    useEffect(() => {
        if (housesById.length > 0) {
            const newHouses = housesById.map((house) => ({
                auctionHouseName: house.name,
                sellerId: house.sellerId,
            }));
            setLocalExcludedHouses(newHouses);
        }
    }, [housesById]);

    useEffect(() => {
        if (excludedHouseIds.length) {
            dispatch(fetchSellersIfNeeded(excludedHouseIds));
        }
    }, [dispatch, excludedHouseIds]);

    const handleRemoveHouse = useCallback((sellerId: number) => {
        setLocalExcludedHouses((prev) => prev.filter((house) => house.sellerId !== sellerId));
    }, []);

    const handleSelectHouse = useCallback((sellerId: string) => {
        setSelectedHousesToExclude((prev) => {
            const newSelection = prev.includes(sellerId) ? prev.filter((id) => id !== sellerId) : [...prev, sellerId];
            return newSelection;
        });
    }, []);

    const handleApplyExcludeHouses = useCallback(async () => {
        const currentHouseIds = localExcludedHouses.map((house) => Number(house.sellerId));
        const housesToInclude = excludedHouseIds.filter((id) => !currentHouseIds.includes(id));

        await dispatch(
            setSearchExclusions({
                excluded: selectedHousesToExclude.length > 0 ? selectedHousesToExclude.map(Number) : undefined,
                facetId: 'auctionHouse',
                included: housesToInclude.length > 0 ? housesToInclude : undefined,
            })
        );
    }, [dispatch, excludedHouseIds, localExcludedHouses, selectedHousesToExclude]);

    return {
        excludedHouseIds,
        handleApplyExcludeHouses,
        handleRemoveHouse,
        handleSelectHouse,
        hasExcludedHousesChanges,
        hasHousesToExcludeChanges,
        housesById,
        housesToExcludeData,
        isLoadingHouses,
        localExcludedHouses,
        selectedHousesToExclude,
    };
};
